/**
 * Binned SAH binary BVH over triangles, linearized to a GPU-friendly node array
 * with a stackless "miss-index" traversal layout.
 *
 * Each linearized node is 8 floats / 32 bytes (interpreted as 8 * 4 = 32 B):
 *   [0..2]  aabb min (vec3)
 *   [3]     misses / hits sentinel  (float-reinterpreted uint32; see below)
 *   [4..6]  aabb max (vec3)
 *   [7]     leaf data / left-child marker
 *
 * We use the Smits stackless layout (a.k.a. "hit-miss" pointers):
 *   - "hit" pointer (next node to visit if ray AABB hit)    -> implicit: nodeIndex + 1
 *   - "miss" pointer (next node to visit if AABB missed)    -> stored in word [3]
 *   - leaf flag: if MSB of word[7] is set, it's a leaf; lower bits encode:
 *        bits [0..23]  -> triangle range start
 *        bits [24..30] -> triangle count (max 127; we cap leaf size at 8)
 *
 * Traversal in WGSL becomes:
 *   var nodeIdx: u32 = 0u;
 *   loop {
 *     if (nodeIdx == 0xFFFFFFFFu) break;
 *     let n = nodes[nodeIdx];
 *     if (aabbHit(ray, n.min, n.max, tMin, tMax)) {
 *       if (n.isLeaf) { testTris(n.triStart, n.triCount); nodeIdx = n.miss; }
 *       else          { nodeIdx = nodeIdx + 1u; }  // visit hit child (left)
 *     } else {
 *       nodeIdx = n.miss;
 *     }
 *   }
 *
 * A miss pointer of 0xFFFFFFFF terminates traversal.
 */

const BIN_COUNT = 16;
const MAX_LEAF_TRIS = 8;
const TRAVERSAL_COST = 1.0;
const INTERSECT_COST = 1.0;

interface Aabb {
  min: [number, number, number];
  max: [number, number, number];
}

function emptyAabb(): Aabb {
  return { min: [Infinity, Infinity, Infinity], max: [-Infinity, -Infinity, -Infinity] };
}

function expandPoint(b: Aabb, x: number, y: number, z: number): void {
  if (x < b.min[0]) b.min[0] = x;
  if (y < b.min[1]) b.min[1] = y;
  if (z < b.min[2]) b.min[2] = z;
  if (x > b.max[0]) b.max[0] = x;
  if (y > b.max[1]) b.max[1] = y;
  if (z > b.max[2]) b.max[2] = z;
}

function expandAabb(dst: Aabb, src: Aabb): void {
  if (src.min[0] < dst.min[0]) dst.min[0] = src.min[0];
  if (src.min[1] < dst.min[1]) dst.min[1] = src.min[1];
  if (src.min[2] < dst.min[2]) dst.min[2] = src.min[2];
  if (src.max[0] > dst.max[0]) dst.max[0] = src.max[0];
  if (src.max[1] > dst.max[1]) dst.max[1] = src.max[1];
  if (src.max[2] > dst.max[2]) dst.max[2] = src.max[2];
}

function surfaceArea(b: Aabb): number {
  const dx = b.max[0] - b.min[0];
  const dy = b.max[1] - b.min[1];
  const dz = b.max[2] - b.min[2];
  if (dx < 0 || dy < 0 || dz < 0) return 0;
  return 2 * (dx * dy + dy * dz + dz * dx);
}

interface BuildNode {
  bounds: Aabb;
  triStart: number;
  triCount: number;
  left: BuildNode | null;
  right: BuildNode | null;
}

interface TriInfo {
  index: number; // triangle index
  centroid: [number, number, number];
  bounds: Aabb;
}

function buildRecursive(tris: TriInfo[], lo: number, hi: number): BuildNode {
  const node: BuildNode = {
    bounds: emptyAabb(),
    triStart: lo,
    triCount: hi - lo,
    left: null,
    right: null,
  };
  for (let i = lo; i < hi; i++) expandAabb(node.bounds, tris[i].bounds);
  const count = hi - lo;
  if (count <= MAX_LEAF_TRIS) return node;

  // Centroid bounds
  const cb = emptyAabb();
  for (let i = lo; i < hi; i++) {
    const c = tris[i].centroid;
    expandPoint(cb, c[0], c[1], c[2]);
  }
  const cbExtent = [cb.max[0] - cb.min[0], cb.max[1] - cb.min[1], cb.max[2] - cb.min[2]];
  let axis = 0;
  if (cbExtent[1] > cbExtent[axis]) axis = 1;
  if (cbExtent[2] > cbExtent[axis]) axis = 2;

  // Too many triangles for a leaf: always split. Giant triangles (a ground slab spanning the whole
  // site) make every split look as costly as a leaf, and a big leaf would lose triangles (the leaf
  // word only holds 127) or crawl on the GPU. SAH picks where; the median is the fallback.
  const sahMid = cbExtent[axis] > 0 ? sahPartition(tris, lo, hi, cb, cbExtent, axis, node.bounds) : -1;
  const mid = sahMid > lo && sahMid < hi ? sahMid : medianPartition(tris, lo, hi, axis);

  node.left = buildRecursive(tris, lo, mid);
  node.right = buildRecursive(tris, mid, hi);
  node.triCount = 0;
  return node;
}

/** Sorts tris[lo, hi) along `axis` and splits them in half. Returns the split index. */
function medianPartition(tris: TriInfo[], lo: number, hi: number, axis: number): number {
  const sorted = tris.slice(lo, hi).sort((a, b) => a.centroid[axis] - b.centroid[axis]);
  for (let i = 0; i < sorted.length; i++) tris[lo + i] = sorted[i];
  return (lo + hi) >> 1;
}

/**
 * Partitions tris[lo, hi) in place at the binned split with the lowest SAH cost. Returns the split
 * index (lo or hi when every triangle landed on one side).
 */
function sahPartition(
  tris: TriInfo[],
  lo: number,
  hi: number,
  cb: Aabb,
  cbExtent: number[],
  axis: number,
  bounds: Aabb,
): number {
  // Bin triangles
  const bins: { count: number; bounds: Aabb }[] = [];
  for (let b = 0; b < BIN_COUNT; b++) bins.push({ count: 0, bounds: emptyAabb() });
  const minA = cb.min[axis];
  const invSpan = BIN_COUNT / cbExtent[axis];
  for (let i = lo; i < hi; i++) {
    const t = tris[i];
    let bi = Math.floor((t.centroid[axis] - minA) * invSpan);
    if (bi < 0) bi = 0;
    if (bi >= BIN_COUNT) bi = BIN_COUNT - 1;
    bins[bi].count += 1;
    expandAabb(bins[bi].bounds, t.bounds);
  }

  // Sweep SAH
  const leftCount = new Array<number>(BIN_COUNT - 1);
  const leftArea = new Array<number>(BIN_COUNT - 1);
  const rightCount = new Array<number>(BIN_COUNT - 1);
  const rightArea = new Array<number>(BIN_COUNT - 1);
  {
    const acc = emptyAabb();
    let accCount = 0;
    for (let i = 0; i < BIN_COUNT - 1; i++) {
      accCount += bins[i].count;
      expandAabb(acc, bins[i].bounds);
      leftCount[i] = accCount;
      leftArea[i] = surfaceArea(acc);
    }
  }
  {
    const acc = emptyAabb();
    let accCount = 0;
    for (let i = BIN_COUNT - 1; i > 0; i--) {
      accCount += bins[i].count;
      expandAabb(acc, bins[i].bounds);
      rightCount[i - 1] = accCount;
      rightArea[i - 1] = surfaceArea(acc);
    }
  }

  const parentArea = surfaceArea(bounds) || 1e-12;
  let bestCost = Infinity;
  let bestSplit = -1;
  for (let i = 0; i < BIN_COUNT - 1; i++) {
    if (leftCount[i] === 0 || rightCount[i] === 0) continue;
    const cost =
      TRAVERSAL_COST + (INTERSECT_COST * (leftCount[i] * leftArea[i] + rightCount[i] * rightArea[i])) / parentArea;
    if (cost < bestCost) {
      bestCost = cost;
      bestSplit = i;
    }
  }
  if (bestSplit < 0) return lo;

  // Partition in-place
  const splitPos = cb.min[axis] + ((bestSplit + 1) * cbExtent[axis]) / BIN_COUNT;
  let mid = lo;
  for (let i = lo; i < hi; i++) {
    if (tris[i].centroid[axis] < splitPos) {
      const tmp = tris[mid];
      tris[mid] = tris[i];
      tris[i] = tmp;
      mid++;
    }
  }
  return mid;
}

interface LinearBvh {
  nodes: Float32Array; // 8 floats per node
  triRemap: Uint32Array; // triangle index in original order
  nodeCount: number;
}

function linearize(root: BuildNode, originalIndices: number[]): LinearBvh {
  const LEAF_FLAG = 0x80000000 >>> 0;
  const TRI_START_MASK = 0x00ffffff;
  const TRI_COUNT_SHIFT = 24;

  // Precompute each node's subtree size so we know the right-child index at visit
  // time and can propagate correct miss pointers through the whole left subtree
  // without a back-patch pass.
  const subtreeSize = new Map<BuildNode, number>();
  function sizeOf(node: BuildNode): number {
    const c = subtreeSize.get(node);
    if (c !== undefined) return c;
    if (!node.left || !node.right) {
      subtreeSize.set(node, 1);
      return 1;
    }
    const s = 1 + sizeOf(node.left) + sizeOf(node.right);
    subtreeSize.set(node, s);
    return s;
  }
  sizeOf(root);

  const orderNodes: BuildNode[] = [];
  const missIndex: number[] = [];

  function visit(node: BuildNode, miss: number): void {
    const myIdx = orderNodes.length;
    orderNodes.push(node);
    missIndex.push(miss);
    if (node.left && node.right) {
      const leftSize = subtreeSize.get(node.left)!;
      const rightIdx = myIdx + 1 + leftSize;
      visit(node.left, rightIdx);
      visit(node.right, miss);
    }
  }
  visit(root, 0xffffffff);

  // Emit flat buffer
  const floatView = new Float32Array(orderNodes.length * 8);
  const uintView = new Uint32Array(floatView.buffer);
  for (let i = 0; i < orderNodes.length; i++) {
    const node = orderNodes[i];
    const miss = missIndex[i] >>> 0;
    const isLeaf = node.left === null || node.right === null;
    let leafWord = 0;
    if (isLeaf) {
      // Out-of-range fields would make triangles silently vanish from the render.
      if (node.triCount > 127 || node.triStart > TRI_START_MASK) {
        throw new Error(`BVH leaf does not fit the GPU layout (start ${node.triStart}, count ${node.triCount})`);
      }
      leafWord = (LEAF_FLAG | ((node.triCount << TRI_COUNT_SHIFT) >>> 0) | node.triStart) >>> 0;
    }
    const base = i * 8;
    floatView[base + 0] = node.bounds.min[0];
    floatView[base + 1] = node.bounds.min[1];
    floatView[base + 2] = node.bounds.min[2];
    uintView[base + 3] = miss;
    floatView[base + 4] = node.bounds.max[0];
    floatView[base + 5] = node.bounds.max[1];
    floatView[base + 6] = node.bounds.max[2];
    uintView[base + 7] = leafWord;
  }

  return {
    nodes: floatView,
    triRemap: new Uint32Array(originalIndices),
    nodeCount: orderNodes.length,
  };
}

export function buildBvh(indices: Uint32Array, positions: Float32Array): LinearBvh {
  const triCount = indices.length / 3;
  if (triCount === 0) {
    return { nodes: new Float32Array(8), triRemap: new Uint32Array(0), nodeCount: 1 };
  }

  const tris: TriInfo[] = new Array(triCount);
  for (let i = 0; i < triCount; i++) {
    const i0 = indices[i * 3] * 3;
    const i1 = indices[i * 3 + 1] * 3;
    const i2 = indices[i * 3 + 2] * 3;
    const x0 = positions[i0],
      y0 = positions[i0 + 1],
      z0 = positions[i0 + 2];
    const x1 = positions[i1],
      y1 = positions[i1 + 1],
      z1 = positions[i1 + 2];
    const x2 = positions[i2],
      y2 = positions[i2 + 1],
      z2 = positions[i2 + 2];
    const b = emptyAabb();
    expandPoint(b, x0, y0, z0);
    expandPoint(b, x1, y1, z1);
    expandPoint(b, x2, y2, z2);
    tris[i] = {
      index: i,
      centroid: [(x0 + x1 + x2) / 3, (y0 + y1 + y2) / 3, (z0 + z1 + z2) / 3],
      bounds: b,
    };
  }

  const root = buildRecursive(tris, 0, triCount);

  // Flatten triRemap (post-partition tris[] holds the new order by index)
  const originalIndices: number[] = new Array(triCount);
  for (let i = 0; i < triCount; i++) originalIndices[i] = tris[i].index;

  return linearize(root, originalIndices);
}
